import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';

const LABEL_MAP = {
  dashboard: 'Home',
  crm: 'CRM',
  marketing: 'Marketing',
  acquisition: 'Acquisition',
  leads: 'Leads',
  campaigns: 'Campaigns',
  deals: 'Deals',
  journeys: 'Journeys',
  segments: 'Segments',
  accounts: 'Accounts',
  contacts: 'Contacts',
  opportunities: 'Opportunities',
  tasks: 'Tasks',
  activities: 'Activities',
  events: 'Events',
  devices: 'Devices',
  messages: 'Broadcast',
  history: 'History',
  phonebook: 'Phonebook',
  autoreply: 'Automation',
  subscription: 'Subscription',
  invoices: 'Invoices',
  settings: 'Settings',
  support: 'Support',
  'api-docs': 'API Docs',
  superadmin: 'Super Admin',
  companies: 'Companies',
  plans: 'Plans',
  'api-keys': 'API Keys',
  coupons: 'Coupons',
  'email-templates': 'Email Templates',
};

// Path segments that are only URL prefixes, not pages of their own.
const NOT_A_PAGE = new Set(['crm', 'marketing', 'acquisition']);

const isId = (s) => s.length > 20 || /^\d/.test(s);
const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');

// Builds the trail from the URL when no explicit items are given.
function fromPath(pathname, customLabels) {
  const parts = pathname.split('/').filter(Boolean);
  const home = parts[0] === 'superadmin' ? '/superadmin/dashboard' : '/dashboard';
  const items = [];
  parts.forEach((seg, i) => {
    if (i === 0) return; // the home item covers /dashboard and /superadmin
    if (seg === 'dashboard' && parts[0] === 'superadmin') return;
    const to = `/${parts.slice(0, i + 1).join('/')}`;
    const label = customLabels[seg] || LABEL_MAP[seg] || (isId(seg) ? 'Details' : titleCase(seg));
    items.push({ label, to: NOT_A_PAGE.has(seg) ? null : to });
  });
  return { home, items };
}

/**
 * SLDS breadcrumb. Icon and text share one baseline; the last item is the
 * current page and is not a link.
 *
 * items: [{ label, to? }] — optional; built from the URL when omitted.
 */
export default function Breadcrumb({ items, customLabels = {}, className = '' }) {
  const location = useLocation();
  const auto = fromPath(location.pathname, customLabels);
  const trail = items || auto.items;

  return (
    <nav aria-label="Breadcrumb" className={`min-w-0 ${className}`}>
      <ol className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-[13px] leading-5">
        <li className="flex items-center">
          <Link to={auto.home} className="inline-flex items-center gap-1.5 text-[#5C5C5C] hover:text-[#0176D3] hover:underline">
            <Home size={14} className="shrink-0" aria-hidden="true" />
            <span>Home</span>
          </Link>
        </li>
        {trail.map((item, i) => {
          const last = i === trail.length - 1;
          return (
            <li key={`${item.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
              <ChevronRight size={14} className="shrink-0 text-[#AEAEAE]" aria-hidden="true" />
              {last || !item.to ? (
                <span className={`truncate max-w-[16rem] ${last ? 'font-semibold text-[#2E2E2E]' : 'text-[#5C5C5C]'}`} aria-current={last ? 'page' : undefined}>
                  {item.label}
                </span>
              ) : (
                <Link to={item.to} className="truncate max-w-[16rem] text-[#5C5C5C] hover:text-[#0176D3] hover:underline">{item.label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
